/**
 * Utilities to manage commercial configuration and logging
 *
 * @module
 */
import * as nested from "nested-property";
import pageUtilities from "./pageUtilities.js";
import utilities from "@trinitymirrordigital/chameleon-utilities/dist/js/utilities.js";
import { log, logFeature, err } from "./logging.js";

const FEATURES = "features";

let fullConfig;
let scriptConfig;

/**
 * Get a page config value. May be overridden by a similar query parameter value
 *
 * @param paramName the config parameter to get value of
 * @returns {*} - the config value, or undefined if page data not available or config value not set
 */
export function getTmConfigParam(paramName) {
  const paramValue =
    pageUtilities.getQueryParameter(paramName) ||
    (window.TMCONFIG || {})[paramName];
  log(`[INFO] Getting TM Config parameter ${paramName}`, paramValue);
  return paramValue;
}

/**
 * Gets a public section parameter value from the page config
 *
 * @param paramName the section parameter to get value of
 * @returns {String} - the section parameter value
 */
export function getPublicSectionParam(paramName) {
  const paramValue =
    pageUtilities.getQueryParameter("publicSectionParams." + paramName) ||
    (getTmConfigParam("publicSectionParams") || {})[paramName];
  log(`[INFO] Getting TM public section parameter ${paramName}`, paramValue);
  return paramValue;
}

/**
 * Cleans feature flag name
 * - removed . or - characters and converts to camel cases
 *
 * @param featureFlag
 * @returns {string}
 */
function cleanFeatureFlag(featureFlag) {
  if (featureFlag.indexOf(".") > -1) {
    return featureFlag
      .toLowerCase()
      .replace(/[.-](.)/g, (match, output) => output.toUpperCase());
  }
  return featureFlag;
}

/**
 * Gets data about a configurable feature, by first looking at a global features object,
 * and then also for section parameters configuring the feature.
 *
 * @param featureKey - name of the feature data to retrieve
 * @returns {*} - feature data value, or undefined if not found
 */
export function getFeatureData(featureKey) {
  const feature = cleanFeatureFlag(featureKey);

  let tmFeature = nested.get(window, `TMFeatures.${feature}`);

  if (tmFeature !== undefined) {
    logFeature(
      FEATURES,
      `[INFO] Feature data (from TMFeatures) for ${feature} : `,
      tmFeature
    );
  }

  const sectionParam = getPublicSectionParam(featureKey);
  if (sectionParam !== undefined) {
    tmFeature = sectionParam;
    logFeature(
      FEATURES,
      `[INFO] Feature data (from section param) for ${feature} : `,
      tmFeature
    );
  }

  logFeature(
    FEATURES,
    `[INFO] Feature data (from resolved sources) for ${feature} : `,
    tmFeature
  );
  return tmFeature;
}

/**
 * Checks if a feature is enabled, by first looking at a global features object,
 * and then also for section parameters enabling the feature.
 *
 * @param featureFlag - name of the feature to check
 * @param defaultStatus - true/false, whether to treat feature as enabled if no feature flag is found
 * @returns {boolean} - true if feature is enabled, false otherwise
 */
function isFeatureEnabled(featureFlag, defaultStatus = false) {
  let featureEnabled = defaultStatus;
  const tmFeature = getFeatureData(featureFlag);

  if (tmFeature !== undefined) {
    featureEnabled = String(tmFeature) === "true";
    logFeature(
      FEATURES,
      `[INFO] Feature flag ${featureFlag} enabled?: ${featureEnabled}`
    );
  }

  logFeature(
    FEATURES,
    `[INFO] Feature ${featureFlag} ${
      featureEnabled ? " is" : " is not"
    } enabled`
  );
  return featureEnabled;
}

/**
 * Checks if a feature is enabled by looking at the config params
 *
 * @returns {boolean} - true if feature is enabled, false otherwise
 */
export function getConfigParam(featureFlag) {
  const queryVal = pageUtilities.getQueryParameter(featureFlag);
  const queryBool =
    queryVal === "true" ? true : queryVal === "false" ? false : undefined;
  if (queryBool !== undefined) {
    logFeature(
      FEATURES,
      `[INFO] Feature flag ${featureFlag} ${
        queryBool ? "enabled" : "disabled"
      } via URL query parameter`
    );
    return queryBool;
  } else if (((window.TMCONFIG || {}).features || {})[featureFlag] === "true") {
    logFeature(
      FEATURES,
      `[INFO] Feature flag ${featureFlag} enabled in config features section parameter`
    );
    return true;
  } else if (getPublicSectionParam(featureFlag) === "true") {
    logFeature(
      FEATURES,
      `[INFO] Feature flag ${featureFlag} enabled in public section parameter`
    );
    return true;
  } else {
    logFeature(FEATURES, `[INFO] Feature flag ${featureFlag} not enabled`);
    return false;
  }
}

/**
 * Retrieve the full config data of the page from the meta tag
 * @function getFullConfig
 * @param {string} elementId The id of the element that contains the config data
 * @returns {Object} Full config object from the meta tag
 */
function getFullConfig(elementId) {
  let metaConfigTag = document.getElementById(elementId);
  if (!metaConfigTag) {
    log(
      "[ERROR] The injection config element cannot be found on the page. [Func: getFullConfig]"
    );
    return;
  }

  return JSON.parse(metaConfigTag.getAttribute("content"));
}

/**
 * Get the full set of ad config adjustments
 * @function getAdjustments
 * @param {string} id The id of the meta tag containing the config adjustments
 * @returns {Object} Adjustment config
 */
function getAdjustments(id) {
  const el = document.getElementById(id);
  if (!el) {
    log(
      "[INFO] The adjustments config element cannot be found on the page. [Func: getAdjustments]"
    );
    return;
  }
  try {
    return JSON.parse(el.getAttribute("content"));
  } catch (e) {
    log(
      `[ERROR] JSON parse of adjustments config failed: ${e} [Func: getAdjustments]`
    );
  }
}

/**
 * Process the set of adjustments for a given entry in the config
 * @function processAdjustments
 * @param {Object} breakpointConfig The full config array for a specific breakpoint
 * @param {number} configIndex The index of the particular config item to process
 * @param {Object} adjustments A set of adjustements to apply
 * @returns {Object} The adjusted breakpointConfig
 */
function processAdjustments(breakpointConfig, configIndex, adjustments) {
  const configItem = breakpointConfig[configIndex];
  if (!adjustments) {
    log(
      "[ERROR] No adjustments found for ",
      configItem,
      " [Func: processAdjustment]"
    );
    return breakpointConfig;
  }
  if (!configItem.allowAdjustments) {
    log(
      "[ERROR] Adjustments not allowed for ",
      configItem,
      " [Func: processAdjustment]"
    );
    return breakpointConfig;
  }
  if (adjustments.remove) {
    if (configItem.allowAdjustments.remove) {
      log(
        "[INFO] Config item removed: ",
        configItem,
        " [Func: processAdjustment]"
      );
      return breakpointConfig.splice(configIndex, 1);
    } else {
      log(
        "[ERROR] Remove adjustment not allowed for ",
        configItem,
        " [Func: processAdjustment]"
      );
    }
  }
  return breakpointConfig;
}

/**
 * Apply adjustments to the ads config
 * @param {Object} config A full ads config
 * @param {Object} adjustments An configuration containing all adjustments to apply
 * @returns {Object} The adjusted ads config
 */
function adjustConfig(config, adjustments) {
  if (!adjustments) {
    return config;
  }
  let configIndex;
  let breakpointConfig;

  Object.keys(adjustments).forEach((breakpoint) => {
    adjustments[breakpoint].forEach((item) => {
      breakpointConfig = config[breakpoint];
      configIndex = breakpointConfig.findIndex((el) => {
        return el.componentType === item.componentType && el.name === item.name;
      });
      if (configIndex > -1) {
        breakpointConfig = processAdjustments(
          breakpointConfig,
          configIndex,
          item.adjustments
        );
      }
    });
  });
  return config;
}

/**
 * Gets the config from a placeholder on the page and parses the JSON
 *
 * @function getConfig
 * @param el {Object} The object to get the config from
 * @returns {Object} The parsed data
 */
function getConfig(el) {
  return JSON.parse(el.getAttribute("data-config"));
}

/**
 * Given a specific breakpoint, gets all the elements at that breakpoint
 * @function getElementsForBreakpoint
 * @param  {string} breakpointName The breakpoint name following the convention in the chameleon-core-config
 * @returns {Array} An array containing all the data for this breakpoint
 */
function getElementsForBreakpoint(breakpointName) {
  let configForBreakpoint;

  log(`[INFO] Getting config elements for breakpoint ${breakpointName}`);

  if (!fullConfig || !fullConfig[breakpointName]) {
    log(
      `[ERROR] The Breakpoint ${breakpointName} is not defined in config. [Func: getElementsForBreakpoint]`
    );
    return [];
  }

  configForBreakpoint = fullConfig[breakpointName];

  log("[INFO] Got config elements", configForBreakpoint);

  return configForBreakpoint;
}

/**
 * Given a specific breakpoint, gets all the elements at that breakpoint matching the requested option type
 * @function getElementByTypeForBreakpoint
 * @param {string} breakpointName The breakpoint name following the convention in the chameleon-core-config
 * @param {string} type The property name of the key value pair to retrieve
 * @returns {Array} An array of all the elements for this breakpoint matching the requested option type
 */
function getElementsByTypeForBreakpoint(breakpointName, type) {
  let filteredConfig;

  if (!fullConfig || !fullConfig[breakpointName]) {
    log(
      `[ERROR] The breakpoint ${breakpointName} is not defined in the config. [Func: getElementsByTypeForBreakpoint]`
    );
    return [];
  }
  let slots = getElementsForBreakpoint(breakpointName);

  if (!Array.isArray(slots)) {
    log(
      "[ERROR] The slots supplied are not an Array. [Func: getElementsByTypeForBreakpoint]"
    );
    return [];
  }

  log("[INFO] The slots for breakpoint are ", slots);

  filteredConfig =
    slots.filter(function (slot) {
      return slot.componentType === type;
    }) || [];

  log(`[INFO] The filtered slots for type ${type} are `, slots);

  return filteredConfig;
}

/**
 * Gets all the elements at the current breakpoint
 * @function getElementsForCurrentBreakpoint
 * @returns {Array} An Array containing all the data for this breakpoint
 */
function getElementsForCurrentBreakpoint() {
  return getElementsForBreakpoint(getBreakpoint());
}

/**
 * Retrieve all configuration data filtered by type of all the breakpoints
 * @function getAllElementsByType
 * @param {string} type The name of the key value to retrieve from the config - E.g  'gpt' || 'taboola'
 * @returns {Array} An array containing the data filtered by type of all the breakpoints
 */
function getAllElementsByType(type) {
  let allSlots = [],
    breakpointSlots;
  for (let breakpoint in fullConfig) {
    if (
      Object.prototype.hasOwnProperty.call(fullConfig, breakpoint) &&
      Array.isArray(fullConfig[breakpoint])
    ) {
      breakpointSlots = getElementsByTypeForBreakpoint(breakpoint, type);
      Array.prototype.push.apply(allSlots, breakpointSlots);
    }
  }

  return allSlots;
}

/**
 * Gets all the elements at the current breakpoint matching the requested option type
 * @function getElementsByTypeForCurrentBreakpoint
 * @param {string} type The property name of the key value pair to retrieve
 * @returns {Array} An array of all the elements for this breakpoint matching the requested option type
 */
function getElementsByTypeForCurrentBreakpoint(type) {
  return getElementsByTypeForBreakpoint(getBreakpoint(), type);
}

function getBreakpoint() {
  if (window.DRAGONFLY) {
    return window.innerWidth >= 1280 ? "desktop" : "baseLayout";
  } else {
    return utilities.getBreakpoint();
  }
}

/**
 * Gets a feature flag value
 * - value will be retrieved from a cms section parameter or a query parameter on the page url
 *
 * @param feature - name of feature
 * @param flagPrefix - prefix of flag in section params - 'feature' (default) or 'public'
 * @param type - transform/force the type of the value - 'int', 'float', 'string', 'bool'
 * @returns {string} - feature flag value
 */
export function getFeatureValue(feature, flagPrefix, type) {
  var featureSectionParam = ((window.TMCONFIG || {})[
    flagPrefix || "features"
  ] || {})[feature];
  var featureQueryParam = utilities.getQueryParam(feature);
  var featureValue =
    featureQueryParam !== null ? featureQueryParam : featureSectionParam;
  if (featureValue) {
    if (type === "int") {
      featureValue = parseInt(featureValue);
    }
    if (type === "float") {
      featureValue = parseFloat(featureValue);
    }
    if (type === "string") {
      featureValue = featureValue.toString();
    }
    if (type === "bool") {
      featureValue = featureValue === "true";
    }
  }
  return featureValue;
}

/**
 * Get the script config, parsed from meta tag. If its already been parsed, just return the data.
 * @returns {*} the config
 */
function getScriptConfig() {
  if (scriptConfig) {
    return scriptConfig;
  }

  const scriptConfigTag = document.getElementById("scriptConfig");
  if (!scriptConfigTag) {
    log(
      "[WARN] The script config element cannot be found on the page. [Func: getScriptConfig]"
    );
    return;
  }

  try {
    return JSON.parse(scriptConfigTag.getAttribute("content"));
  } catch (e) {
    log(`[ERROR] Error returning script config: ${e}`);
  }
}

/**
 * Converts a string to a Regex object
 * - may be a regex with // delimiters and modifiers or a simple regex string without delimiters
 *
 * @param {String} string to convert to regex
 * @returns {RegExp} Regular expression object
 */
function stringToRegex(str) {
  let regexp;

  const regParts = str.match(/^\/(.*?)\/([gim]*)$/);
  if (regParts) {
    // the parsed pattern had delimiters and possibly modifiers.
    regexp = new RegExp(regParts[1], regParts[2]);
  } else {
    // we got pattern string without delimiters
    regexp = new RegExp(str);
  }

  return regexp;
}

/**
 * Retrieve the full config data of the page from the meta tag
 * @function init
 * @param {string} elementId The ID of the config meta tag to grab config data from
 */
function init(elementId) {
  fullConfig = getFullConfig(elementId);
  fullConfig = adjustConfig(
    fullConfig,
    getAdjustments(elementId + "-adjustments")
  );
  scriptConfig = getScriptConfig(scriptConfig);
}

init("advertisingConfig");

// express overrides
function setConfig(full, script) {
  fullConfig = full;
  scriptConfig = script;
}

export default {
  adjustConfig,
  err,
  getAllElementsByType,
  getConfig,
  getElementsByTypeForBreakpoint,
  getElementsByTypeForCurrentBreakpoint,
  getElementsForBreakpoint,
  getElementsForCurrentBreakpoint,
  getFeatureData,
  getFullConfig,
  getPublicSectionParam,
  getScriptConfig,
  getTmConfigParam,
  init,
  isFeatureEnabled,
  getConfigParam,
  log,
  logFeature,
  stringToRegex,
  getFeatureValue,
  // express overrides
  setConfig,
};
